import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PDFDocument } from "pdf-lib";
import { FileOutput, Plus, Trash2, RotateCcw, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CUR, lineTotal, buildInvoiceDoc } from "./invoicePdf";
import { BaseAddressSelect } from "./BaseAddressSelect";

const today = () => new Date().toISOString().slice(0, 10);
const userName = (u) => (u?.first_name && u?.last_name ? `${u.first_name} ${u.last_name}` : u?.display_name || "");

const US_STATES = "AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC".split(" ");
const US_STATE_NAMES = ["ALABAMA","ALASKA","ARIZONA","ARKANSAS","CALIFORNIA","COLORADO","CONNECTICUT","DELAWARE","FLORIDA","GEORGIA","HAWAII","IDAHO","ILLINOIS","INDIANA","IOWA","KANSAS","KENTUCKY","LOUISIANA","MAINE","MARYLAND","MASSACHUSETTS","MICHIGAN","MINNESOTA","MISSISSIPPI","MISSOURI","MONTANA","NEBRASKA","NEVADA","NEW HAMPSHIRE","NEW JERSEY","NEW MEXICO","NEW YORK","NORTH CAROLINA","NORTH DAKOTA","OHIO","OKLAHOMA","OREGON","PENNSYLVANIA","RHODE ISLAND","SOUTH CAROLINA","SOUTH DAKOTA","TENNESSEE","TEXAS","UTAH","VERMONT","VIRGINIA","WASHINGTON","WEST VIRGINIA","WISCONSIN","WYOMING"];
const US_CITIES = ["WICHITA FALLS","WICHITA","CHICAGO","HOUSTON","DALLAS","PHOENIX","SEATTLE","DENVER","ATLANTA","BOSTON","MIAMI","NASHVILLE","MEMPHIS","BALTIMORE","MILWAUKEE","ALBUQUERQUE","TUCSON","SACRAMENTO","OMAHA","TULSA","OAKLAND","CLEVELAND","PITTSBURGH","CINCINNATI","ANCHORAGE","HONOLULU","DETROIT","MINNEAPOLIS","NEW ORLEANS","LAS VEGAS","LOS ANGELES","SAN FRANCISCO","SAN DIEGO","SAN ANTONIO","SAN JOSE","FORT WORTH","EL PASO","INDIANAPOLIS","CHARLOTTE","JACKSONVILLE","PHILADELPHIA","NEW YORK","BROOKLYN","LOUISVILLE","OKLAHOMA CITY","KANSAS CITY","SALT LAKE CITY","ST. LOUIS","ST LOUIS","TAMPA","ORLANDO","FORT LAUDERDALE","BAKERSFIELD","FRESNO","BUFFALO","ROCHESTER","SPOKANE","BOISE","BILLINGS","FARGO","DES MOINES","LITTLE ROCK","BATON ROUGE","KNOXVILLE","CHATTANOOGA","SAVANNAH","RALEIGH","GREENSBORO","RICHMOND","NORFOLK","HARTFORD","PROVIDENCE","ALBANY","SYRACUSE","GRAND RAPIDS","TOLEDO","AKRON","DAYTON","LEXINGTON","AMARILLO","LUBBOCK","CORPUS CHRISTI","AUSTIN","RENO","TACOMA","EVERETT","EUGENE","COLORADO SPRINGS","PUEBLO","CHEYENNE","RAPID CITY","SIOUX FALLS","DULUTH","GREEN BAY","MADISON","PEORIA","ROCKFORD","EVANSVILLE","FORT WAYNE","SOUTH BEND","ERIE","ALLENTOWN","SCRANTON","BRIDGEPORT","WORCESTER","MANCHESTER","BANGOR"];
const US_WORD_RE = /(^|[^A-Z])(USA|U\.S\.A\.?|U\.S\.|UNITED STATES)([^A-Z]|$)/;
const US_STATE_ZIP_RE = new RegExp(`(^|[^A-Z])(${US_STATES.join("|")})[ ,.]*\\d{5}(-\\d{4})?([^0-9]|$)`);
const US_COMMA_STATE_RE = new RegExp(`,\\s*(${US_STATES.join("|")})\\.?([^A-Z]|$)`);
const US_TRAILING_STATE_RE = new RegExp(`(^|[^A-Z])(${US_STATES.join("|")})\\.?\\s*$`);
const US_STATE_NAME_RE = new RegExp(`(^|[^A-Z])(${US_STATE_NAMES.join("|")})([^A-Z]|$)`);
const US_CITY_RE = new RegExp(`(^|[^A-Z])(${US_CITIES.join("|")})([^A-Z]|$)`);
export const isUSAddress = (txt) => {
  const t = String(txt || "").toUpperCase();
  return (
    US_WORD_RE.test(t) ||
    US_STATE_ZIP_RE.test(t) ||
    US_COMMA_STATE_RE.test(t) ||
    US_TRAILING_STATE_RE.test(t) ||
    US_STATE_NAME_RE.test(t) ||
    US_CITY_RE.test(t)
  );
};

const MAX_ROWS = 8;
const blankRow = () => ({ pieces: "", dg: false, un: "", desc: "", cls: "", pg: "", other: "", weight: "", hs: "", unit: "" });

const makeDefaults = (user) => ({
  date: today(),
  shipCode: "",
  shipName: "Summit Air",
  shipAddress: "100 Dickins Street",
  shipCity: "Yellowknife",
  shipState: "NT",
  shipPostal: "X1A 3T2",
  shipCountry: "Canada",
  consCode: "",
  consName: "",
  consAddress: "",
  consCity: "",
  consState: "",
  consPostal: "",
  consCountry: "",
  declaredVal: "",
  declaredPer: "",
  freightTerms: "prepaid",
  routing: "",
  transferPoint: "",
  thirdCode: "",
  thirdNameAddr: "",
  po: "",
  ref: "",
  shippersNo: "",
  totalPieces: "",
  dimL: "",
  dimW: "",
  dimH: "",
  cubicFeet: "",
  totalWeight: "",
  dimWeight: "",
  codAmount: "",
  codFee: "",
  bottomShipper: "Summit Air",
  perName: userName(user),
  invoiceNo: "",
  currency: "USD",
  countryManufacture: "",
});

const cityLine = (city, state, postal, country) => {
  const a = [city, [state, postal].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [a, country].filter(Boolean).join(", ");
};

const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function ManitoulinBOL() {
  const { user } = useAuth();
  const [m, setM] = useState(() => makeDefaults(user));
  const [items, setItems] = useState([blankRow()]);
  const [busy, setBusy] = useState(false);
  const [usOverride, setUsOverride] = useState(null);
  const set = (k) => (e) => setM((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const setItem = (i, k, v) => setItems((its) => its.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const reset = () => { setM(makeDefaults(user)); setItems([blankRow()]); setUsOverride(null); };

  const autoUS = useMemo(
    () => isUSAddress(`${m.consCity}, ${m.consState} ${m.consPostal}, ${m.consCountry}`),
    [m.consCity, m.consState, m.consPostal, m.consCountry]
  );
  const usShipment = usOverride === null ? autoUS : usOverride;
  const invItems = items.filter((it) => (it.desc || "").trim() || (it.pieces || "").trim());
  const invSubtotal = invItems.reduce((s, it) => s + (lineTotal({ qty: it.pieces, unit: it.unit }) || 0), 0);
  const sym = CUR[m.currency].sym;

  const applyBaseAddress = (c) => {
    const addr = String(c.address || "").trim();
    const street = addr.split(",")[0].trim();
    const [city, state] = String(c.name || "").split(",").map((s) => s.trim());
    const postal = (addr.match(/[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d/) || [""])[0];
    setM((s) => ({
      ...s,
      shipAddress: street || s.shipAddress,
      shipCity: city || s.shipCity,
      shipState: state || s.shipState,
      shipPostal: postal || s.shipPostal,
      shipCountry: "Canada",
    }));
  };

  const exportPdf = async () => {
    if (busy) return;
    if (usShipment && !m.countryManufacture.trim()) {
      toast.error("Country of Manufacture is required for US shipments.");
      return;
    }
    setBusy(true);
    try {
      const bytes = await (await fetch("/media/manitoulin_bol.pdf")).arrayBuffer();
      const pdf = await PDFDocument.load(bytes);
      const form = pdf.getForm();
      const names = form.getFields().map((f) => f.getName());
      const setT = (re, val, multiline = false) => {
        if (!val) return;
        const n = names.find((x) => re.test(x));
        if (!n) return;
        try {
          const f = form.getTextField(n);
          if (multiline) f.enableMultiline();
          f.setText(String(val));
        } catch {}
      };

      // Bill of Lading No. intentionally left BLANK
      const [yy, mo, dd] = (m.date || "").split("-");
      setT(/^D\s+J$/, dd);
      setT(/^M$/, mo);
      setT(/^Y\s+A$/, yy);
      setT(/^SHIPPER\s+EXPÉDITEUR CUSTOMER CODE/, m.shipCode);
      setT(/^NAME\s+NOM$/, m.shipName);
      setT(/^ADDRESS\s+ADRESSE$/, m.shipAddress);
      setT(/^CITY\s+VILLE$/, cityLine(m.shipCity, m.shipState, m.shipPostal, m.shipCountry));
      setT(/^CONSIGNEE\s+CONSIGNATAIRE CUSTOMER CODE/, m.consCode);
      setT(/^NAME\s+NOM_2$/, m.consName);
      setT(/^ADDRESS\s+ADRESSE_2$/, m.consAddress);
      setT(/^CITY\s+VILLE_2$/, cityLine(m.consCity, m.consState, m.consPostal, m.consCountry));
      setT(/^Valeur déclarée$/, m.declaredVal);
      setT(/^Per$/, m.declaredPer);
      setT(/^prepaid$/, m.freightTerms === "prepaid" ? "X" : "");
      setT(/^collect$/, m.freightTerms === "collect" ? "X" : "");
      setT(/^ROUTING\s+CARRIER/, [m.routing, m.transferPoint].filter(Boolean).join("   •   Transfer: "));
      setT(/^BILL THIRD PARTY/, m.thirdNameAddr, true);
      setT(/^Code du Client$/, m.thirdCode);
      setT(/^PO$/, m.po);
      setT(/^REF$/, m.ref);
      setT(/^SHIPPERS$/, m.shippersNo);
      setT(/^NO OF PIECES$/, m.totalPieces);
      setT(/^LENGTH/, m.dimL);
      setT(/^WIDTH/, m.dimW);
      setT(/^HEIGHT/, m.dimH);
      setT(/^TOTAL CUBIC/, m.cubicFeet);
      setT(/^TOTAL WEIGHT/, m.totalWeight);
      setT(/^DIMENSIONAL WEIGHT/, m.dimWeight);
      setT(/^COD\$$/, m.codAmount);
      setT(/^SHIPPER\s+EXPÉDITEUR$/, m.bottomShipper);
      setT(/^PER_2$/, m.perName);
      if (m.codFee) {
        const cbName = m.codFee === "prepaid" ? "toggle_1" : names.find((x) => /^COD FEE COLLECT/.test(x));
        try { form.getCheckBox(cbName).check(); } catch {}
      }
      items.slice(0, MAX_ROWS).forEach((it, i) => {
        const n = i + 1;
        setT(new RegExp(`^Pieces and Type.*Row${n}$`), it.pieces);
        setT(new RegExp(`^DG MD XRow${n}$`), it.dg ? "X" : "");
        setT(new RegExp(`^UN${n}$`), it.un);
        setT(n === 1 ? /^Shipping Name And Descript.*UN$/ : new RegExp(`^Shipping Name And Descript.*UN_${n}$`), it.desc);
        setT(n === 1 ? /^Class ClasseUN$/ : new RegExp(`^Class ClasseUN_${n}$`), it.cls);
        setT(n === 1 ? /^Packing Group.*UN$/ : new RegExp(`^Packing Group.*UN_${n}$`), it.pg);
        setT(n === 1 ? /^Other AutreUN$/ : new RegExp(`^Other AutreUN_${n}$`), it.other);
        setT(n === 1 ? /^Weight PoidsUN$/ : new RegExp(`^Weight PoidsUN_${n}$`), it.weight);
      });
      try { form.updateFieldAppearances(); form.flatten(); } catch {}

      if (usShipment) {
        const invData = {
          shipper: [m.shipName, m.shipAddress, cityLine(m.shipCity, m.shipState, m.shipPostal, ""), m.shipCountry || "Canada"].filter(Boolean).join("\n"),
          consignee: [m.consName, m.consAddress, cityLine(m.consCity, m.consState, m.consPostal, ""), m.consCountry].filter(Boolean).join("\n"),
          notifyParty: "",
          date: m.date,
          invoiceNo: m.invoiceNo,
          customerPo: m.po,
          dateExport: m.date,
          origin: "Canada",
          blAwb: "",
          finalDest: cityLine(m.consCity, m.consState, "", m.consCountry),
          exportRoute: m.routing || "Manitoulin Transport",
          termsSale: "",
          termsPayment: "",
          freightTerms: m.freightTerms,
          marks: "",
          currency: m.currency,
          countryManufacture: m.countryManufacture,
          handling: "",
          freight: "",
          misc: "",
          certify: "\"WE HEREBY CERTIFY THIS INVOICE TO BE TRUE AND CORRECT.\"",
          signedName: userName(user),
          signedTitle: "Stores",
          signedDate: today(),
          exportedTo: "",
        };
        const invDoc = await buildInvoiceDoc(invData, invItems.map((it) => ({ qty: it.pieces, desc: it.desc, hs: it.hs, unit: it.unit })));
        const invPdf = await PDFDocument.load(invDoc.output("arraybuffer"));
        for (let c = 0; c < 3; c++) {
          const pages = await pdf.copyPages(invPdf, invPdf.getPageIndices());
          pages.forEach((pg) => pdf.addPage(pg));
        }
      }

      const out = await pdf.save();
      const blob = new Blob([out], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Manitoulin BOL${m.po ? ` ${m.po}` : ""} - ${m.date}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(usShipment ? "PDF exported — BOL + 3 commercial invoice copies" : "PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "h-9";
  return (
    <div data-testid="manitoulin-bol-form">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={reset} data-testid="mbol-reset-btn">
          <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
        </Button>
        <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={exportPdf} disabled={busy} data-testid="mbol-export-btn">
          <FileOutput className="w-4 h-4 mr-1.5" /> {busy ? "Exporting…" : "Export PDF"}
        </Button>
      </div>
      <p className="text-xs text-gray-500 mt-2">Fills the official Manitoulin Transport Bill of Lading. The Bill of Lading No. is left blank on purpose.</p>

      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <div className="border border-summit-hairline rounded-lg bg-white p-4 grid grid-cols-2 gap-3 content-start">
          <div className="col-span-2 flex items-center justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Shipper / Expéditeur</span>
            <BaseAddressSelect testId="mbol-shipper-autofill" onSelect={applyBaseAddress} />
          </div>
          <F label="Name" className="col-span-2"><Input className={inputCls} value={m.shipName} onChange={set("shipName")} data-testid="mbol-ship-name" /></F>
          <F label="Address" className="col-span-2"><Input className={inputCls} value={m.shipAddress} onChange={set("shipAddress")} data-testid="mbol-ship-address" /></F>
          <F label="City"><Input className={inputCls} value={m.shipCity} onChange={set("shipCity")} data-testid="mbol-ship-city" /></F>
          <F label="Province / State"><Input className={inputCls} value={m.shipState} onChange={set("shipState")} data-testid="mbol-ship-state" /></F>
          <F label="Postal Code"><Input className={inputCls} value={m.shipPostal} onChange={set("shipPostal")} data-testid="mbol-ship-postal" /></F>
          <F label="Country"><Input className={inputCls} value={m.shipCountry} onChange={set("shipCountry")} data-testid="mbol-ship-country" /></F>
          <F label="Customer code (optional)"><Input className={inputCls} value={m.shipCode} onChange={set("shipCode")} data-testid="mbol-ship-code" /></F>
          <F label="Date"><Input className={inputCls} type="date" value={m.date} onChange={set("date")} data-testid="mbol-date" /></F>
        </div>
        <div className={`border rounded-lg bg-white p-4 grid grid-cols-2 gap-3 content-start ${usShipment ? "border-amber-400" : "border-summit-hairline"}`}>
          <div className="col-span-2 flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Consignee / Consignataire</span>
            {usShipment && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest" data-testid="mbol-us-badge">
                <Flag className="w-3 h-3" /> US shipment
              </span>
            )}
          </div>
          <F label="Name" className="col-span-2"><Input className={inputCls} value={m.consName} onChange={set("consName")} data-testid="mbol-cons-name" /></F>
          <F label="Address" className="col-span-2"><Input className={inputCls} value={m.consAddress} onChange={set("consAddress")} placeholder="e.g. 450 Airport Rd" data-testid="mbol-cons-address" /></F>
          <F label="City"><Input className={inputCls} value={m.consCity} onChange={set("consCity")} placeholder="e.g. Wichita" data-testid="mbol-cons-city" /></F>
          <F label="Province / State"><Input className={inputCls} value={m.consState} onChange={set("consState")} placeholder="e.g. KS" data-testid="mbol-cons-state" /></F>
          <F label="Postal Code"><Input className={inputCls} value={m.consPostal} onChange={set("consPostal")} placeholder="e.g. 67209" data-testid="mbol-cons-postal" /></F>
          <F label="Country"><Input className={inputCls} value={m.consCountry} onChange={set("consCountry")} placeholder="e.g. USA" data-testid="mbol-cons-country" /></F>
          <F label="Customer code (optional)"><Input className={inputCls} value={m.consCode} onChange={set("consCode")} data-testid="mbol-cons-code" /></F>
          <div className="col-span-2 flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="mbol-us-toggle"
              checked={usShipment}
              onChange={(e) => setUsOverride(e.target.checked)}
              data-testid="mbol-us-toggle"
            />
            <label htmlFor="mbol-us-toggle" className="text-xs text-gray-600 cursor-pointer">
              US shipment — adds 3 Commercial Invoice copies to the export
              {usOverride === null && autoUS && <span className="text-amber-600 font-medium"> (auto-detected)</span>}
              {usOverride !== null && <span className="text-gray-400"> (manual)</span>}
            </label>
          </div>
        </div>
      </div>

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white p-4 grid grid-cols-2 md:grid-cols-4 gap-3 content-start">
        <div className="col-span-2 md:col-span-4 font-mono text-[10px] uppercase tracking-widest text-gray-500">Shipment details</div>
        <F label="Declared valuation $"><Input className={inputCls} value={m.declaredVal} onChange={set("declaredVal")} data-testid="mbol-declared-val" /></F>
        <F label="Per"><Input className={inputCls} value={m.declaredPer} onChange={set("declaredPer")} data-testid="mbol-declared-per" /></F>
        <F label="Freight charges">
          <div className="flex items-center gap-4 h-9">
            {["prepaid", "collect"].map((v) => (
              <label key={v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="mbolFreight" checked={m.freightTerms === v} onChange={() => setM((s) => ({ ...s, freightTerms: v }))} data-testid={`mbol-freight-${v}`} />
                <span className="font-mono text-[11px] uppercase tracking-widest">{v}</span>
              </label>
            ))}
          </div>
        </F>
        <F label="Routing / Carrier"><Input className={inputCls} value={m.routing} onChange={set("routing")} data-testid="mbol-routing" /></F>
        <F label="Transfer point"><Input className={inputCls} value={m.transferPoint} onChange={set("transferPoint")} data-testid="mbol-transfer" /></F>
        <F label="P.O. #"><Input className={inputCls} value={m.po} onChange={set("po")} data-testid="mbol-po" /></F>
        <F label="REF #"><Input className={inputCls} value={m.ref} onChange={set("ref")} data-testid="mbol-ref" /></F>
        <F label="Shipper's #"><Input className={inputCls} value={m.shippersNo} onChange={set("shippersNo")} data-testid="mbol-shippers-no" /></F>
        <F label="Third party — customer code"><Input className={inputCls} value={m.thirdCode} onChange={set("thirdCode")} data-testid="mbol-third-code" /></F>
        <F label="Third party — name & address" className="col-span-2 md:col-span-3">
          <Textarea rows={2} className="text-sm" value={m.thirdNameAddr} onChange={set("thirdNameAddr")} data-testid="mbol-third-name" />
        </F>
      </div>

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-summit-hairline bg-summit-canvas">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Items ({items.length}/{MAX_ROWS})</span>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={items.length >= MAX_ROWS} onClick={() => setItems((its) => [...its, blankRow()])} data-testid="mbol-add-item-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="border-b border-summit-hairline">
                {["Pieces & type", "DG", "UN #", "Shipping name & description", "Class", "Pkg grp", "Other", "Weight", ""].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-gray-500 first:pl-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-summit-hairline last:border-0 align-top" data-testid={`mbol-item-row-${i}`}>
                  <td className="pl-4 pr-1 py-1.5 w-28"><Input className="h-8 text-xs" value={it.pieces} onChange={(e) => setItem(i, "pieces", e.target.value)} placeholder="2 Crates" data-testid={`mbol-item-pieces-${i}`} /></td>
                  <td className="px-1 py-1.5 w-10 text-center">
                    <input type="checkbox" className="mt-2.5" checked={it.dg} onChange={(e) => setItem(i, "dg", e.target.checked)} title="Dangerous goods" data-testid={`mbol-item-dg-${i}`} />
                  </td>
                  <td className="px-1 py-1.5 w-24"><Input className="h-8 text-xs" value={it.un} onChange={(e) => setItem(i, "un", e.target.value)} data-testid={`mbol-item-un-${i}`} /></td>
                  <td className="px-1 py-1.5"><Input className="h-8 text-xs" value={it.desc} onChange={(e) => setItem(i, "desc", e.target.value)} placeholder="e.g. Aircraft parts — support frame" data-testid={`mbol-item-desc-${i}`} /></td>
                  <td className="px-1 py-1.5 w-20"><Input className="h-8 text-xs" value={it.cls} onChange={(e) => setItem(i, "cls", e.target.value)} data-testid={`mbol-item-class-${i}`} /></td>
                  <td className="px-1 py-1.5 w-20"><Input className="h-8 text-xs" value={it.pg} onChange={(e) => setItem(i, "pg", e.target.value)} data-testid={`mbol-item-pg-${i}`} /></td>
                  <td className="px-1 py-1.5 w-24"><Input className="h-8 text-xs" value={it.other} onChange={(e) => setItem(i, "other", e.target.value)} data-testid={`mbol-item-other-${i}`} /></td>
                  <td className="px-1 py-1.5 w-24"><Input className="h-8 text-xs" value={it.weight} onChange={(e) => setItem(i, "weight", e.target.value)} placeholder="79 kg" data-testid={`mbol-item-weight-${i}`} /></td>
                  <td className="px-1 py-1.5 w-8">
                    <button className="p-1 rounded hover:bg-rose-50 text-rose-400" disabled={items.length === 1} onClick={() => setItems((its) => its.filter((_, j) => j !== i))} data-testid={`mbol-item-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <div className="border border-summit-hairline rounded-lg bg-white p-4 grid grid-cols-3 gap-3 content-start">
          <div className="col-span-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Totals & dimensions</div>
          <F label="Total no. of pieces"><Input className={inputCls} value={m.totalPieces} onChange={set("totalPieces")} data-testid="mbol-total-pieces" /></F>
          <F label="Length"><Input className={inputCls} value={m.dimL} onChange={set("dimL")} data-testid="mbol-dim-l" /></F>
          <F label="Width"><Input className={inputCls} value={m.dimW} onChange={set("dimW")} data-testid="mbol-dim-w" /></F>
          <F label="Height"><Input className={inputCls} value={m.dimH} onChange={set("dimH")} data-testid="mbol-dim-h" /></F>
          <F label="Total cubic feet"><Input className={inputCls} value={m.cubicFeet} onChange={set("cubicFeet")} data-testid="mbol-cubic-feet" /></F>
          <F label="Total weight"><Input className={inputCls} value={m.totalWeight} onChange={set("totalWeight")} data-testid="mbol-total-weight" /></F>
          <F label="Dimensional weight"><Input className={inputCls} value={m.dimWeight} onChange={set("dimWeight")} data-testid="mbol-dim-weight" /></F>
        </div>
        <div className="border border-summit-hairline rounded-lg bg-white p-4 grid grid-cols-2 gap-3 content-start">
          <div className="col-span-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">C.O.D. & signature</div>
          <F label="C.O.D. amount $"><Input className={inputCls} value={m.codAmount} onChange={set("codAmount")} data-testid="mbol-cod-amount" /></F>
          <F label="C.O.D. fee">
            <div className="flex items-center gap-3 h-9">
              {[["", "None"], ["prepaid", "Prepaid"], ["collect", "Collect"]].map(([v, l]) => (
                <label key={l} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name="mbolCodFee" checked={m.codFee === v} onChange={() => setM((s) => ({ ...s, codFee: v }))} data-testid={`mbol-cod-fee-${l.toLowerCase()}`} />
                  <span className="font-mono text-[11px] uppercase tracking-widest">{l}</span>
                </label>
              ))}
            </div>
          </F>
          <F label="Shipper (bottom of form)"><Input className={inputCls} value={m.bottomShipper} onChange={set("bottomShipper")} data-testid="mbol-bottom-shipper" /></F>
          <F label="Per (signed by)"><Input className={inputCls} value={m.perName} onChange={set("perName")} data-testid="mbol-per-name" /></F>
        </div>
      </div>

      {usShipment && (
        <div className="mt-4 border border-amber-400 rounded-lg bg-amber-50/60 p-4" data-testid="mbol-us-panel">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-amber-600" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-amber-800">US shipment — Commercial Invoice (3 copies added to the export)</span>
          </div>
          <p className="text-xs text-amber-800/80 mt-1">Shipper, consignee, date, P.O. and item descriptions auto-populate from the BOL above. Add the invoice-only details here.</p>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <F label="Commercial Invoice No."><Input className="h-9 bg-white" value={m.invoiceNo} onChange={set("invoiceNo")} data-testid="mbol-invoice-no" /></F>
            <F label="Currency">
              <select className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm" value={m.currency} onChange={set("currency")} data-testid="mbol-currency">
                {Object.keys(CUR).map((c) => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Country of Manufacture (required)" className="col-span-2">
              <Input className="h-9 bg-white" value={m.countryManufacture} onChange={set("countryManufacture")} placeholder="e.g. Canada" data-testid="mbol-country-manufacture" />
            </F>
          </div>
          {invItems.length > 0 && (
            <div className="mt-3 border border-amber-200 rounded-md bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-amber-100">
                    {["Item (from BOL)", "H.S. Number", `Unit price (${sym})`, "Total"].map((h) => (
                      <th key={h} className="text-left px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    if (!(it.desc || "").trim() && !(it.pieces || "").trim()) return null;
                    const t = lineTotal({ qty: it.pieces, unit: it.unit });
                    return (
                      <tr key={i} className="border-b border-amber-50 last:border-0">
                        <td className="px-3 py-1.5 text-xs text-gray-600">{it.pieces || "1"} · {it.desc || "—"}</td>
                        <td className="px-2 py-1.5 w-36"><Input className="h-8 text-xs" value={it.hs} onChange={(e) => setItem(i, "hs", e.target.value)} placeholder="88033000" data-testid={`mbol-item-hs-${i}`} /></td>
                        <td className="px-2 py-1.5 w-32"><Input className="h-8 text-xs" type="number" min="0" step="0.01" value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} data-testid={`mbol-item-unit-${i}`} /></td>
                        <td className="px-3 py-1.5 w-28 font-mono text-xs text-right whitespace-nowrap pt-3">{t === null ? "—" : `${sym}${t.toFixed(2)}`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex justify-end px-3 py-2 border-t border-amber-100 bg-amber-50/50">
                <span className="font-mono text-xs" data-testid="mbol-inv-subtotal">Invoice total: {m.currency} {sym}{invSubtotal.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
